import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  InputAdornment,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import octopusInstance from "../../services/octopus/octopusInstance";
import {
  getExchangeCode,
  exchangeSpecificStringifiedNumber,
} from "../../services/exchanges";
import {
  placeGTTOrderAction,
  modifyGTTOrderAction,
} from "../../store/actions/orders";
import OrderWindowHeader from "./parts/OrderWindowHeader";
import MarketDataPanel from "./parts/MarketDataPanel";
import { isValidMultiple } from "../../hooks/useOrderFormUtils";

// GTT orders are always CNC for equity, NRML for derivatives (matches captur logic).
const getGTTProduct = (exchange) =>
  ["NFO", "BFO", "CDS", "MCX", "DCSX"].includes(exchange) ? "NRML" : "CNC";

// GTT orders expire one year from today (YYYY-MM-DD), matching captur.
const getExpiryDate = () => {
  const d = new Date();
  const year = d.getFullYear() + 1;
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Tolerance-based tick-multiple check (same as the order window) — a plain
// `value % step === 0` fails for valid prices because of float rounding,
// e.g. 105.35 % 0.01 === 0.00999999...
const isMultipleOf = (value, step) => {
  const v = parseFloat(value);
  const s = parseFloat(step);
  if (isNaN(v) || isNaN(s) || s <= 0) return true;
  return isValidMultiple(v, s);
};

const GTTModal = (props) => {
  const {
    open,
    type,
    trading_symbol,
    display_name,
    ltp,
    exchange,
    token,
    lotsize,
    tick_size,
    is_modify,
    gttData,
    onClose,
  } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const tickSize = parseFloat(tick_size) || 0.05;
  const boardLot = parseInt(lotsize, 10) || 1;

  const [orderSide, setOrderSide] = useState(type || "BUY");
  const [actionType, setActionType] = useState("single_order");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [marketData, setMarketData] = useState({
    ltp,
    ltt: null,
    bestBid: null,
    bestAsk: null,
  });

  // Single order fields
  const [quantity, setQuantity] = useState(boardLot);
  const [price, setPrice] = useState(ltp || 0);
  const [triggerPrice, setTriggerPrice] = useState(ltp || 0);

  // OCO fields (SELL only)
  const [slQuantity, setSlQuantity] = useState(boardLot);
  const [slPrice, setSlPrice] = useState(ltp || 0);
  const [slTriggerPrice, setSlTriggerPrice] = useState(ltp || 0);
  const [tgtQuantity, setTgtQuantity] = useState(boardLot);
  const [tgtPrice, setTgtPrice] = useState(ltp || 0);
  const [tgtTriggerPrice, setTgtTriggerPrice] = useState(ltp || 0);

  const initialisedRef = useRef(false);

  // Initialise form state whenever the modal opens.
  useEffect(() => {
    if (!open) {
      initialisedRef.current = false;
      return;
    }
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    const baseQty = boardLot || 1;
    if (is_modify && gttData) {
      const data = gttData;
      setOrderSide(data.order_side || type || "BUY");
      setActionType(data.action_type || "single_order");
      setQuantity(data.quantity || baseQty);
      setPrice(data.price || ltp || 0);
      setTriggerPrice(data.trigger_price || ltp || 0);
      setSlQuantity(data.sl_order_quantity || baseQty);
      setSlPrice(data.sl_order_price || ltp || 0);
      setSlTriggerPrice(data.sl_trigger_price || ltp || 0);
      setTgtQuantity(data.target_quantity || data.quantity || baseQty);
      setTgtPrice(data.target_price || data.price || ltp || 0);
      setTgtTriggerPrice(data.target_trigger_price || data.trigger_price || ltp || 0);
    } else {
      setOrderSide(type || "BUY");
      setActionType("single_order");
      setQuantity(baseQty);
      setPrice(ltp || 0);
      setTriggerPrice(ltp || 0);
      setSlQuantity(baseQty);
      setSlPrice(ltp || 0);
      setSlTriggerPrice(ltp || 0);
      setTgtQuantity(baseQty);
      setTgtPrice(ltp || 0);
      setTgtTriggerPrice(ltp || 0);
    }
    setErrors({});
  }, [open, is_modify, gttData, type, ltp, boardLot]);

  // OCO is only available for SELL orders (matches captur). Force back to single
  // when the user is on the BUY side.
  useEffect(() => {
    if (orderSide === "BUY" && actionType === "oco") {
      setActionType("single_order");
    }
  }, [orderSide, actionType]);

  // Live bid/ask/LTP subscription, same detailed feed the order window uses.
  useEffect(() => {
    if (!open || !token || !exchange) return;
    const handler = octopusInstance.wsHandler({
      messageType: "DetailMarketDataMessage",
      subscriptionLocation: `gtt-modal-${token}`,
      payload: {
        exchangeCode: getExchangeCode(exchange),
        instrumentToken: token,
      },
    });
    handler.subscribe(({ msg }) => {
      setMarketData((prev) => {
        const next = {
          ltp: msg.lastTradedPrice || prev.ltp,
          ltt: msg.lastTradedTime || prev.ltt,
          bestBid: msg.bestBidPrice || prev.bestBid,
          bestAsk: msg.bestAskPrice || prev.bestAsk,
        };
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
    });
    return () => handler.unsubscribe();
  }, [open, token, exchange]);

  const product = getGTTProduct(exchange);
  const isBuy = orderSide === "BUY";

  const formatMarketValue = (value) =>
    value ? exchangeSpecificStringifiedNumber(exchange, value) : null;

  // ---- Validation -------------------------------------------------------
  const validateQty = (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n <= 0) return "Quantity must be greater than 0";
    if (boardLot > 1 && n % boardLot !== 0)
      return `Quantity must be a multiple of ${boardLot}`;
    return "";
  };

  const validatePrice = (val) => {
    const n = parseFloat(val);
    if (isNaN(n) || n === 0) return "Invalid price";
    if (!isMultipleOf(n, tickSize)) return `Price must be a multiple of ${tickSize}`;
    return "";
  };

  // Trigger validation: check if it's a valid number and multiple of tick size.
  const validateTrigger = (val, legPrice, side) => {
    const n = parseFloat(val);
    if (isNaN(n) || n < 0) return "Trigger price cannot be negative";
    if (!isMultipleOf(n, tickSize))
      return `Trigger price must be a multiple of ${tickSize}`;
    return "";
  };

  const validateAll = () => {
    const e = {};
    if (actionType === "single_order") {
      e.quantity = validateQty(quantity);
      e.price = validatePrice(price);
      e.trigger_price = validateTrigger(triggerPrice, price, orderSide);
    } else {
      // OCO (SELL only): stop loss + target legs.
      e.sl_quantity = validateQty(slQuantity);
      e.sl_price = validatePrice(slPrice);
      e.sl_trigger_price = validateTrigger(slTriggerPrice, slPrice, orderSide);
      e.tgt_quantity = validateQty(tgtQuantity);
      e.tgt_price = validatePrice(tgtPrice);
      e.tgt_trigger_price = validateTrigger(tgtTriggerPrice, tgtPrice, orderSide);
    }
    const cleaned = Object.fromEntries(
      Object.entries(e).filter(([, v]) => v)
    );
    setErrors(cleaned);
    return Object.keys(cleaned).length === 0;
  };

  // ---- Steppers ---------------------------------------------------------
  const stepPrice = (setter, current, dir) => {
    const cur = parseFloat(current) || 0;
    const next = parseFloat((cur + dir * tickSize).toFixed(2));
    setter(next < 0 ? 0 : next);
  };
  const stepQty = (setter, current, dir) => {
    const cur = parseInt(current, 10) || 0;
    const inc = exchange === "MCX" ? 1 : boardLot;
    const next = cur + dir * inc;
    setter(next < inc ? inc : next);
  };

  const buildPayload = useCallback(() => {
    const order = {
      client_id: localStorage.getItem("login_id"),
      exchange,
      order_type: "LIMIT",
      instrument_token: token,
      order_side: orderSide,
      product,
      user_order_id: String(Date.now()),
      market_protection_percentage: 0,
      device: "web",
      quantity:
        actionType === "oco" ? Number(tgtQuantity) : Number(quantity),
      price: actionType === "oco" ? Number(tgtPrice) : Number(price),
      trigger_price:
        actionType === "oco" ? Number(tgtTriggerPrice) : Number(triggerPrice),
      sl_order_price: actionType === "single_order" ? 0 : Number(slPrice),
      sl_order_quantity:
        actionType === "single_order" ? 0 : Number(slQuantity),
      sl_trigger_price:
        actionType === "single_order" ? 0 : Number(slTriggerPrice),
      target_price: actionType === "oco" ? Number(tgtPrice) : Number(price),
      target_trigger_price:
        actionType === "oco" ? Number(tgtTriggerPrice) : Number(triggerPrice),
      target_quantity:
        actionType === "oco" ? Number(tgtQuantity) : Number(quantity),
    };
    const payload = {
      expiry_time: getExpiryDate(),
      action_type: actionType,
      order,
    };
    if (is_modify && gttData?.id) payload.id = gttData.id;
    return payload;
  }, [
    exchange,
    token,
    orderSide,
    product,
    actionType,
    quantity,
    price,
    triggerPrice,
    slQuantity,
    slPrice,
    slTriggerPrice,
    tgtQuantity,
    tgtPrice,
    tgtTriggerPrice,
    is_modify,
    gttData,
  ]);

  const handleSubmit = async () => {
    if (!validateAll()) return;
    setLoading(true);
    try {
      const payload = buildPayload();
      const action = is_modify
        ? modifyGTTOrderAction(payload)
        : placeGTTOrderAction(payload);
      const res = await dispatch(action);
      if (res?.status === "success") {
        enqueueSnackbar(
          res.message ||
            `GTT order ${is_modify ? "modified" : "placed"} successfully`,
          { variant: "success" }
        );
        onClose();
      } else {
        enqueueSnackbar(res?.message || "Failed to submit GTT order", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to submit GTT order", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSideChange = (_e, val) => {
    if (val !== null && !is_modify) setOrderSide(val);
  };

  // ---- Reusable field renderer -----------------------------------------
  const renderField = ({
    label,
    value,
    onChange,
    onStepUp,
    onStepDown,
    error,
    isQty = false,
    adornment,
  }) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        size="small"
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
          if (e.key === "ArrowUp") {
            e.preventDefault();
            onStepUp();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            onStepDown();
          }
        }}
        error={Boolean(error)}
        helperText={error || (isQty ? "" : `Tick: ${tickSize}`)}
        InputProps={{
          startAdornment: adornment ? (
            <InputAdornment position="start">
              <Typography variant="caption" color="text.secondary">
                {adornment}
              </Typography>
            </InputAdornment>
          ) : undefined,
          inputProps: {
            step: isQty ? (exchange === "MCX" ? 1 : boardLot) : tickSize,
            min: 0,
            style: { fontWeight: "bold" },
          },
        }}
        sx={{
          "& .MuiFormHelperText-root": { fontSize: "0.6rem", mx: 0.5 },
        }}
      />
    </Box>
  );

  const sectionBox = (title, children) => (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
        p: { xs: 1, sm: 1.5 },
        mb: 1.5,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: "0.8rem" }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", gap: { xs: 0.75, sm: 1 }, alignItems: "flex-start" }}>
        {children}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      keepMounted
    >
      <DialogTitle
        sx={{
          backgroundColor: isBuy
            ? theme.palette.buy.background
            : theme.palette.sell.background,
          background: isBuy
            ? `linear-gradient(135deg, ${theme.palette.buy.background} 0%, ${theme.palette.buy.light} 100%)`
            : `linear-gradient(135deg, ${theme.palette.sell.background} 0%, ${theme.palette.sell.light} 100%)`,
          color: theme.palette.common.white,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: isMobile ? 1.5 : 2,
          boxShadow: theme.shadows[4],
          transition: "background-color 0.3s ease",
        }}
      >
        <OrderWindowHeader
          isMobile={isMobile}
          orderSide={orderSide}
          onTypeChange={handleSideChange}
          isModify={is_modify}
          type={type}
          displayName={display_name || trading_symbol}
          onClose={onClose}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: isMobile ? 1.5 : 2 }}>
        {/* Market data (bid / ltp / ltt / ask) — same panel as the order window */}
        <MarketDataPanel
          bestBid={marketData.bestBid}
          ltp={marketData.ltp}
          ltt={marketData.ltt}
          bestAsk={marketData.bestAsk}
          formatValue={formatMarketValue}
        />
        {/* Trigger type + product/order type row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 1.5,
            mt: 0.5,
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}
            >
              Trigger Type
            </Typography>
            <Tabs
              value={actionType}
              onChange={(_e, val) => !is_modify && setActionType(val)}
              variant="fullWidth"
              sx={{
                minHeight: 26,
                "& .MuiTabs-flexContainer": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2.5,
                  p: 0.25,
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {[
                { value: "single_order", label: "Single", disabled: false },
                { value: "oco", label: "OCO", disabled: isBuy || is_modify },
              ].map((opt) => (
                <Tab
                  key={opt.value}
                  value={opt.value}
                  label={opt.label}
                  disableRipple
                  disabled={opt.disabled}
                  sx={{
                    borderRadius: 1.5,
                    fontSize: "0.65rem",
                    minHeight: 26,
                    height: 26,
                    padding: "2px 8px",
                    textTransform: "none",
                    fontWeight: 600,
                    color:
                      actionType === opt.value
                        ? "#FFFFFF !important"
                        : theme.palette.text.primary,
                    backgroundColor:
                      actionType === opt.value
                        ? isBuy
                          ? theme.palette.success.main
                          : theme.palette.error.main
                        : "transparent",
                    "&:hover": {
                      backgroundColor:
                        actionType === opt.value
                          ? isBuy
                            ? theme.palette.success.main
                            : theme.palette.error.main
                          : alpha(theme.palette.primary.main, 0.2),
                      color:
                        actionType === opt.value
                          ? "#FFFFFF !important"
                          : theme.palette.text.primary,
                    },
                    "&.Mui-disabled": { color: theme.palette.text.disabled },
                  }}
                />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ display: "flex", gap: 1, pb: 0.25 }}>
            {[product, "LIMIT"].map((chip) => (
              <Box
                key={chip}
                sx={{
                  px: 1.25,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 1.5,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                {chip}
              </Box>
            ))}
          </Box>
        </Box>

        {actionType === "single_order"
          ? sectionBox("Single Order", (
              <>
                {renderField({
                  label: "Quantity",
                  value: quantity,
                  onChange: setQuantity,
                  onStepUp: () => stepQty(setQuantity, quantity, 1),
                  onStepDown: () => stepQty(setQuantity, quantity, -1),
                  error: errors.quantity,
                  isQty: true,
                })}
                {renderField({
                  label: isBuy ? "Buy Price" : "Sell Price",
                  value: price,
                  onChange: setPrice,
                  onStepUp: () => stepPrice(setPrice, price, 1),
                  onStepDown: () => stepPrice(setPrice, price, -1),
                  error: errors.price,
                  adornment: "₹",
                })}
                {renderField({
                  label: "Trigger Price",
                  value: triggerPrice,
                  onChange: setTriggerPrice,
                  onStepUp: () => stepPrice(setTriggerPrice, triggerPrice, 1),
                  onStepDown: () => stepPrice(setTriggerPrice, triggerPrice, -1),
                  error: errors.trigger_price,
                  adornment: "₹",
                })}
              </>
            ))
          : (
            <>
              {sectionBox("Stop Loss", (
                <>
                  {renderField({
                    label: "Quantity",
                    value: slQuantity,
                    onChange: setSlQuantity,
                    onStepUp: () => stepQty(setSlQuantity, slQuantity, 1),
                    onStepDown: () => stepQty(setSlQuantity, slQuantity, -1),
                    error: errors.sl_quantity,
                    isQty: true,
                  })}
                  {renderField({
                    label: "Price",
                    value: slPrice,
                    onChange: setSlPrice,
                    onStepUp: () => stepPrice(setSlPrice, slPrice, 1),
                    onStepDown: () => stepPrice(setSlPrice, slPrice, -1),
                    error: errors.sl_price,
                    adornment: "₹",
                  })}
                  {renderField({
                    label: "Trigger Price",
                    value: slTriggerPrice,
                    onChange: setSlTriggerPrice,
                    onStepUp: () => stepPrice(setSlTriggerPrice, slTriggerPrice, 1),
                    onStepDown: () => stepPrice(setSlTriggerPrice, slTriggerPrice, -1),
                    error: errors.sl_trigger_price,
                    adornment: "₹",
                  })}
                </>
              ))}
              {sectionBox("Target", (
                <>
                  {renderField({
                    label: "Quantity",
                    value: tgtQuantity,
                    onChange: setTgtQuantity,
                    onStepUp: () => stepQty(setTgtQuantity, tgtQuantity, 1),
                    onStepDown: () => stepQty(setTgtQuantity, tgtQuantity, -1),
                    error: errors.tgt_quantity,
                    isQty: true,
                  })}
                  {renderField({
                    label: "Price",
                    value: tgtPrice,
                    onChange: setTgtPrice,
                    onStepUp: () => stepPrice(setTgtPrice, tgtPrice, 1),
                    onStepDown: () => stepPrice(setTgtPrice, tgtPrice, -1),
                    error: errors.tgt_price,
                    adornment: "₹",
                  })}
                  {renderField({
                    label: "Trigger Price",
                    value: tgtTriggerPrice,
                    onChange: setTgtTriggerPrice,
                    onStepUp: () => stepPrice(setTgtTriggerPrice, tgtTriggerPrice, 1),
                    onStepDown: () => stepPrice(setTgtTriggerPrice, tgtTriggerPrice, -1),
                    error: errors.tgt_trigger_price,
                    adornment: "₹",
                  })}
                </>
              ))}
            </>
          )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: "divider",
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: "stretch",
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size={isMobile ? "large" : "small"}
          fullWidth={isMobile}
          sx={{ minWidth: 90 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          size={isMobile ? "large" : "medium"}
          fullWidth={isMobile}
          sx={{
            backgroundColor: isBuy ? "success.main" : "error.main",
            "&:hover": { backgroundColor: isBuy ? "success.dark" : "error.dark" },
            minWidth: 160,
          }}
        >
          {loading
            ? "Submitting..."
            : is_modify
            ? "Modify GTT"
            : `Place ${isBuy ? "Buy" : "Sell"} GTT`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GTTModal;
